import { Pen, SystemTypes } from "../../../Definitions";
import { Section, Station } from "../../../Definitions/newSectionModalInterfaces";
import { CreatePanelLocation } from "../../../Services";
import { CreateStation, CreateTerminalLink } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/createLfSectionCall";

function isPen(content: Pen | Station): content is Pen {
  return (content as Pen).penId !== undefined;
}

export function isStation(content: Pen | Station): content is Station {
  return (content as Station).stationId !== undefined;
}

export const transformData = (contents: Pen[] | Station[], section: Section, systemType: SystemTypes): CreatePanelLocation[] => {
  const filteredContent = contents.filter((content) => {
    if (systemType === SystemTypes.freedaLf) {
      return (content as Pen).penId !== null;
    }

    if (systemType === SystemTypes.freedaEsf_WalkThrough) {
      return (content as Station).stationId !== null;
    }
    return false;
  })
  // Change value dependent on systemType
  const lfValue = filteredContent.reduce((agg, content) => {
    if (systemType === SystemTypes.freedaLf && isPen(content)) {

      const terminals = [{ device_id: content.io ?? 0, terminal_type: content.feederOutputConfig?.terminal_type_id, config: content.feederOutputConfig, port: content.feederOutput ?? 0 }] // TODO: Terminal types

      const waterTerminal = { device_id: content.io ?? 0, terminal_type: content.waterOutputConfig?.terminal_type_id, config: content.waterOutputConfig, port: content.waterOutput ?? 0 };
      const activatorTerminal = { device_id: content.io ?? 0, terminal_type: (content).activatorInputConfig?.terminal_type_id, config: (content).activatorInputConfig, port: ((content).activatorInput ?? 0) + 100 };

      if (section.water) {
        terminals.push(waterTerminal);
      }

      if (section.activator && systemType === SystemTypes.freedaLf) {
        terminals.push(activatorTerminal);
      }

      agg.push({
        visual: content.visual ?? "",
        location_name: content.name ?? "",
        stations: [{
          visual: content.visual ?? "",
          name: content.name ?? "",
          terminals: terminals,
          station_type: 1
        }]
      })
    }
    return agg;
  }, [] as CreatePanelLocation[])

  const esfWalkThrough = filteredContent.reduce((agg, content) => {    
    if (isStation(content)) {
      agg[0].stations.push({
        visual: content.visual ?? "",
        name: content.name ?? "",
        terminals: Object.values(content.terminals).map((terminal) => ({ device_id: terminal.io ?? 0, terminal_type: terminal.terminalType ?? 0, config: terminal.terminalConfig ?? undefined, port: terminal.port ?? 0 })),
        station_type: section.stationType === 2 || undefined ? 2 : 3
      })
    }
    return agg
  }, [{ visual: contents.at(0)?.visual ?? "", location_name: section.name ?? "", stations: [] as CreateStation[] }])

  const esfWalkIn = null;

  if (systemType === SystemTypes.freedaEsf_WalkThrough) {
    return esfWalkThrough
  }
  return lfValue;
}

export function lfExtractTerminalLinks(section: Section): CreateTerminalLink[] {
  const terminalLinks: CreateTerminalLink[] = [];

  for (let i = 0; i < section.contents.length; i++) {
    const location = section.contents[i] as Pen;

    if (!location.io || !location.feederOutput) {
      //TODO: log err?
      continue;
    }

    const feederTerminal: CreateTerminalLink = {
      device_id: location.io,
      port: location.feederOutput,
    }
    let waterTerminal: CreateTerminalLink | undefined;
    let activatorTerminal: CreateTerminalLink | undefined;

    if (location.waterOutput) {
      waterTerminal = {
        device_id: location.io,
        port: location.waterOutput,
        parent_device_id: feederTerminal.device_id,
        parent_port: feederTerminal.port
      }

      feederTerminal.next_device_id = waterTerminal.device_id;
      feederTerminal.next_port = waterTerminal.port;

      terminalLinks.push(waterTerminal);
    }

    if (location.activatorInput) {
      activatorTerminal = {
        device_id: location.io,
        port: location.activatorInput + 100, //Notice: input ports run from 100-199
        next_device_id: feederTerminal.device_id,
        next_port: feederTerminal.port
      }

      feederTerminal.parent_device_id = activatorTerminal.device_id;
      feederTerminal.parent_port = activatorTerminal.port;

      terminalLinks.push(activatorTerminal);
    }

    if (waterTerminal || activatorTerminal) { //No reason to create the link if there is nothing to be linked
      terminalLinks.push(feederTerminal);
    }

  }

  return terminalLinks;
}


export function esfExtractTerminalLinks(section: Section): CreateTerminalLink[] {
  return (section.contents as Station[]).flatMap((station) => {
    const stationLinks: CreateTerminalLink[] = [];
    const motorTerminals = Object.values(station.terminals).filter((terminal) => terminal.__terminalType === "feederOutput").sort((a, b) => (a.terminalOrder ?? 0) - (b.terminalOrder ?? 0)); // Sort by terminal order to ensure correct chaining

    motorTerminals.forEach((motorTerminal, index, array) => {
      if (index === 0) {
        stationLinks.push({
          device_id: motorTerminal.io ?? 0,
          port: motorTerminal.port ?? 0,
          parent_device_id: station.terminals["readerInput"]?.io ?? 0,
          parent_port: station.terminals["readerInput"]?.port ?? 0,
          next_device_id: array.length > 1 ? array[index + 1].io ?? 0 : station.terminals["waterOutput"]?.io ?? undefined,
          next_port: array.length > 1 ? array[index + 1].port ?? 0 : station.terminals["waterOutput"]?.port ?? undefined,
        })
        return
      }
      if (index + 1 === array.length) {
        stationLinks.push({
          device_id: motorTerminal.io ?? 0,
          port: motorTerminal.port ?? 0,
          parent_device_id: array[index - 1].io ?? 0,
          parent_port: array[index - 1].port ?? 0,
          next_device_id: station.terminals["waterOutput"]?.io ?? undefined,
          next_port: station.terminals["waterOutput"]?.port ?? undefined,
        })
        return
      }
      stationLinks.push({
        device_id: motorTerminal.io ?? 0,
        port: motorTerminal.port ?? 0,
        parent_device_id: array[index - 1].io ?? 0,
        parent_port: array[index - 1].port ?? 0,
        next_device_id: array[index + 1].io ?? 0,
        next_port: array[index + 1].port ?? 0,
      }
      )
    })

    // // reader Link
    stationLinks.push({
      device_id: station.terminals["readerInput"]?.io ?? 0,
      port: station.terminals["readerInput"]?.port ?? 0,
      parent_device_id: station.terminals["gateSensor"]?.io ?? undefined,
      parent_port: station.terminals["gateSensor"]?.port ?? undefined,
      next_device_id: station.terminals["feederOutput-0"]?.io ?? 0,
      next_port: station.terminals["feederOutput-0"]?.port ?? 0,
    })

    // // Gate sensor
    if (station.terminals["gateSensor"]?.io && station.terminals["gateSensor"]?.port) {
      stationLinks.push({
        device_id: station.terminals["gateSensor"].io,
        port: station.terminals["gateSensor"].port,
        parent_device_id: station.terminals["readerInput"]?.io ?? 0,
        parent_port: station.terminals["readerInput"]?.port ?? 0,
        next_device_id: station.terminals["gateMotorOutput"]?.io ?? undefined,
        next_port: station.terminals["gateMotorOutput"]?.port ?? undefined,
      })
    }

    // // Gate motor

    if (station.terminals["gateMotorOutput"]?.io && station.terminals["gateMotorOutput"]?.port) {
      stationLinks.push({
        device_id: station.terminals["gateMotorOutput"].io,
        port: station.terminals["gateMotorOutput"].port,
        parent_device_id: station.terminals["gateSensor"]?.io ?? undefined,
        parent_port: station.terminals["gateSensor"]?.port ?? undefined,
        next_device_id: station.terminals["buttonInput"]?.io ?? undefined,
        next_port: station.terminals["buttonInput"]?.port ?? undefined
      })
    }


    // // water
    if (station.terminals["waterOutput"]?.io && station.terminals["waterOutput"]?.port) {
      stationLinks.push({
        device_id: station.terminals["waterOutput"]?.io ?? 0,
        port: station.terminals["waterOutput"]?.port ?? 0,
        parent_device_id: station.terminals[`feederOutput-${motorTerminals.length - 1}`]?.io ?? undefined,
        parent_port: station.terminals[`feederOutput-${motorTerminals.length - 1}`]?.port ?? undefined,
      })
    }

    // // button
    if (station.terminals["buttonInput"]?.io && station.terminals["buttonInput"]?.port) {
      stationLinks.push({
        device_id: station.terminals["buttonInput"]?.io ?? 0,
        port: station.terminals["buttonInput"]?.port ?? 0,
        parent_device_id: station.terminals["readerInput"]?.io ?? 0,
        parent_port: station.terminals["readerInput"]?.port ?? 0,
      })
    }

    return stationLinks;
  })
}
